import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
// Automation preset library + lightweight Scenario editor (ADR-030 variant B).
//
// Grid library + Drawer Master-Detail: legacy and graph scenarios use editors
// matching their persisted contracts; active switches stay optimistic with rollback.
import { useCallback, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@govard/i18n-core/react';
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Skeleton,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';

import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LIST_SURFACE_STYLE } from '../../../shared/ui/list-styles.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { useAutomations, useToggleAutomation } from '../hooks/useAutomations.js';

import { ScenarioEditorDrawer } from '../components/ScenarioEditorDrawer.js';
import { GraphScenarioDrawer } from '../components/GraphScenarioDrawer.js';
import { GRAPH_FOLLOW_UP_PRESET_KEY } from '../lib/graphFollowUpPreset.js';
import '../components/automations.css';

const { Text, Paragraph } = Typography;

/** Preset key of the graph-based follow-up: no step editor exists for it. */
const GRAPH_PRESET_KEY = GRAPH_FOLLOW_UP_PRESET_KEY;

/** Preset key of the scheduled Morning My Day: no schedule editor exists for it. */
const MY_DAY_PRESET_KEY = 'morning-my-day';

/** Preset key of the scheduled Conversion digest (settings-driven consent). */
const CONVERSION_DIGEST_PRESET_KEY = 'conversion-digest';

/** Stage 5B.7: both implementations render as the ONE canonical Follow-up card. */
const LEGACY_PRESET_KEY = 'follow-up-after-3-days';

function useLabels() {
  const { t } = useTranslation();
  return {
    title: t('crm.automations.title'),
    subtitle: t('crm.automations.subtitle'),
    loading: t('crm.automations.loading'),
    emptyTitle: t('crm.automations.empty_title'),
    emptyDescription: t('crm.automations.empty_description'),
    errorTitle: t('crm.automations.error_title'),
    enabled: t('crm.automations.enabled'),
    disabled: t('crm.automations.disabled'),
    toggleOn: t('crm.automations.toggle_on'),
    toggleOff: t('crm.automations.toggle_off'),
    trigger: t('crm.automations.trigger'),
    condition: t('crm.automations.condition'),
    outcome: t('crm.automations.outcome'),
    runCenterLink: t('crm.automations.run_center_link'),
    runCenterAria: (name: string) => t('crm.automations.run_center_aria', { name }),
    toggleAria: (name: string) => t('crm.automations.toggle_aria', { name }),
    editAria: (name: string) => t('crm.automations.edit_aria', { name }),
    create: t('crm.automations.create'),
    createGraph: t('crm.graph_scenario_editor.create'),
    editGraph: t('crm.graph_scenario_editor.edit_action'),
    updated: t('crm.automations.updated'),
    updateFailed: t('crm.automations.update_failed'),
    refresh: t('crm.automations.refresh'),
    refreshAria: t('crm.automations.refresh_aria'),
    presetFollowUpName: t('crm.automations.preset.follow_up.name'),
    presetFollowUpDescription: t('crm.automations.preset.follow_up.description'),
    presetFollowUpTrigger: t('crm.automations.preset.follow_up.trigger'),
    presetFollowUpCondition: t('crm.automations.preset.follow_up.condition'),
    presetFollowUpOutcome: t('crm.automations.preset.follow_up.outcome'),
    presetMyDayName: t('crm.automations.preset.my_day.name'),
    presetMyDayDescription: t('crm.automations.preset.my_day.description'),
    presetMyDayTrigger: t('crm.automations.preset.my_day.trigger'),
    presetMyDaySchedule: t('crm.automations.preset.my_day.schedule'),
    presetMyDayCondition: t('crm.automations.preset.my_day.condition'),
    presetMyDayOutcome: t('crm.automations.preset.my_day.outcome'),
    presetConversionDigestName: t('crm.automations.preset.conversion_digest.name'),
    presetConversionDigestDescription: t('crm.automations.preset.conversion_digest.description'),
    presetConversionDigestTrigger: t('crm.automations.preset.conversion_digest.trigger'),
    presetConversionDigestSchedule: t('crm.automations.preset.conversion_digest.schedule'),
    presetConversionDigestCondition: t('crm.automations.preset.conversion_digest.condition'),
    presetConversionDigestOutcome: t('crm.automations.preset.conversion_digest.outcome'),
    triggerTypeEvent: t('crm.automations.trigger_type.event'),
    triggerTypeManual: t('crm.automations.trigger_type.manual'),
    triggerTypeScheduled: t('crm.automations.trigger_type.scheduled'),
    more: t('crm.automations.more'),
    genericCondition: t('crm.automations.generic.condition'),
    genericOutcome: t('crm.automations.generic.outcome'),
    edit: t('crm.scenario_editor.edit'),
    scheduledEditUnavailable: t('crm.automations.scheduled_edit_unavailable'),
  };
}

function getPresetDisplay(
  scenario: {
    presetKey: string | null;
    name: string;
    description: string | null;
    triggerType: string;
  },
  labels: ReturnType<typeof useLabels>,
): {
  displayName: string;
  displayDescription: string | null;
  displayTrigger: string;
  /** Local schedule line, present only for scheduled presets. */
  displaySchedule?: string | undefined;
  displayCondition: string;
  displayOutcome: string;
} {
  // Stage 5B.7: the legacy and graph keys are two implementations of the
  // same logical automation. Both render the ONE canonical Follow-up card
  // with locale-driven labels — never the stored (possibly English) row
  // name and never the historical "(graph)" suffix.
  if (scenario.presetKey === LEGACY_PRESET_KEY || scenario.presetKey === GRAPH_PRESET_KEY) {
    return {
      displayName: labels.presetFollowUpName,
      displayDescription: labels.presetFollowUpDescription,
      displayTrigger: labels.presetFollowUpTrigger,
      displayCondition: labels.presetFollowUpCondition,
      displayOutcome: labels.presetFollowUpOutcome,
    };
  }
  if (scenario.presetKey === MY_DAY_PRESET_KEY) {
    return {
      displayName: labels.presetMyDayName,
      displayDescription: labels.presetMyDayDescription,
      displayTrigger: labels.presetMyDayTrigger,
      displaySchedule: labels.presetMyDaySchedule,
      displayCondition: labels.presetMyDayCondition,
      displayOutcome: labels.presetMyDayOutcome,
    };
  }
  if (scenario.presetKey === CONVERSION_DIGEST_PRESET_KEY) {
    return {
      displayName: labels.presetConversionDigestName,
      displayDescription: labels.presetConversionDigestDescription,
      displayTrigger: labels.presetConversionDigestTrigger,
      displaySchedule: labels.presetConversionDigestSchedule,
      displayCondition: labels.presetConversionDigestCondition,
      displayOutcome: labels.presetConversionDigestOutcome,
    };
  }
  const triggerMap: Record<string, string> = {
    event: labels.triggerTypeEvent,
    manual: labels.triggerTypeManual,
    scheduled: labels.triggerTypeScheduled,
  };
  return {
    displayName: scenario.name,
    displayDescription: scenario.description,
    displayTrigger: triggerMap[scenario.triggerType] ?? scenario.triggerType,
    displayCondition: labels.genericCondition,
    displayOutcome: labels.genericOutcome,
  };
}

export function AutomationsPage(): ReactElement {
  const labels = useLabels();
  const scope = useCurrentScope();
  const canManage = scope !== null && ['owner', 'admin'].includes(scope.role);
  const { data, isLoading, isError, error, refetch, isFetching } = useAutomations();
  const toggle = useToggleAutomation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [graphEditor, setGraphEditor] = useState<{ open: boolean; scenarioId: string | null }>({
    open: false,
    scenarioId: null,
  });

  const handleToggle = useCallback(
    (id: string, nextActive: boolean) => {
      if (!canManage) return;
      toggle.mutate({ id, isActive: nextActive });
    },
    [canManage, toggle],
  );

  if (isLoading) {
    return (
      <main className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 p-4 md:p-6">
        <Text type="secondary">{labels.loading}</Text>
        <Skeleton active paragraph={{ rows: 4 }} />
        <Skeleton active paragraph={{ rows: 6 }} />
      </main>
    );
  }

  if (isError) {
    return (
      <main className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 p-4 md:p-6">
        <ErrorState title={labels.errorTitle} error={error} onRetry={() => void refetch()} />
      </main>
    );
  }

  const items = data ?? [];
  const persistedCanonicalGraph = items.find(
    (scenario) => scenario.presetKey === GRAPH_FOLLOW_UP_PRESET_KEY && !scenario.isProjected,
  );
  const openCanonicalGraphEditor = (): void =>
    setGraphEditor({ open: true, scenarioId: persistedCanonicalGraph?.id ?? null });

  if (items.length === 0) {
    return (
      <main className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 p-4 md:p-6">
        <PageHeader
          title={labels.title}
          subtitle={labels.subtitle}
          testId="automations-page-header"
          actions={
            <Space wrap>
              {canManage ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openCanonicalGraphEditor}
                  aria-label={labels.createGraph}
                >
                  {labels.create}
                </Button>
              ) : null}
              <Button
                aria-label={labels.refreshAria}
                icon={<ReloadOutlined />}
                loading={isFetching}
                onClick={() => void refetch()}
              >
                {labels.refresh}
              </Button>
            </Space>
          }
        />
        <div style={LIST_SURFACE_STYLE}>
          {/* Тихое empty (spec D): без иллюстраций — заголовок и строка-подпись. */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              padding: '20px 16px',
            }}
          >
            <Text strong>{labels.emptyTitle}</Text>
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
              {labels.emptyDescription}
            </span>
          </div>
          {canManage ? (
            <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 20 }}>
              <Button
                type="primary"
                onClick={openCanonicalGraphEditor}
                aria-label={labels.createGraph}
              >
                {labels.createGraph}
              </Button>
            </div>
          ) : null}
        </div>
        <ScenarioEditorDrawer
          scenarioId={selectedId}
          open={selectedId !== null}
          onClose={() => setSelectedId(null)}
        />
        <GraphScenarioDrawer
          scenarioId={graphEditor.scenarioId}
          open={graphEditor.open}
          onClose={() => setGraphEditor({ open: false, scenarioId: null })}
        />
      </main>
    );
  }

  return (
    <main className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 p-4 md:p-6">
      <PageHeader
        title={labels.title}
        subtitle={labels.subtitle}
        testId="automations-page-header"
        actions={
          <Space wrap>
            {canManage ? (
              <Button
                type="primary"
                icon={persistedCanonicalGraph ? <EditOutlined /> : <PlusOutlined />}
                onClick={openCanonicalGraphEditor}
                aria-label={persistedCanonicalGraph ? labels.editGraph : labels.createGraph}
              >
                {persistedCanonicalGraph ? labels.editGraph : labels.create}
              </Button>
            ) : null}
            <Button
              aria-label={labels.refreshAria}
              icon={<ReloadOutlined />}
              loading={isFetching}
              onClick={() => void refetch()}
            >
              {labels.refresh}
            </Button>
          </Space>
        }
      />

      {toggle.isError ? <Alert type="error" showIcon title={labels.updateFailed} /> : null}

      <Row gutter={[16, 16]}>
        {items.map((scenario) => {
          const display = getPresetDisplay(scenario, labels);
          return (
            <Col key={scenario.id} xs={24} md={12} xl={8}>
              <Card
                data-testid={`automation-card-${scenario.presetKey ?? scenario.id}`}
                className="automation-card"
                size="small"
                style={{ height: '100%' }}
                title={
                  <Space>
                    <Text strong>{display.displayName}</Text>
                    <EntityStatusChip tone={scenario.isActive ? 'success' : 'default'}>
                      {scenario.isActive ? labels.enabled : labels.disabled}
                    </EntityStatusChip>
                  </Space>
                }
                extra={
                  canManage ? (
                    <Switch
                      aria-label={labels.toggleAria(display.displayName)}
                      checked={scenario.isActive}
                      disabled={toggle.isPending}
                      loading={toggle.isPending && toggle.variables?.id === scenario.id}
                      onChange={(checked) => handleToggle(scenario.id, checked)}
                      checkedChildren={labels.enabled}
                      unCheckedChildren={labels.disabled}
                    />
                  ) : (
                    <EntityStatusChip tone={scenario.isActive ? 'success' : 'default'}>
                      {scenario.isActive ? labels.enabled : labels.disabled}
                    </EntityStatusChip>
                  )
                }
              >
                <Space orientation="vertical" size={8} style={{ width: '100%' }}>
                  {display.displayDescription ? (
                    <Paragraph
                      key={display.displayDescription}
                      type="secondary"
                      aria-label={display.displayDescription}
                      title={display.displayDescription}
                      ellipsis={{ rows: 2, expandable: true, symbol: labels.more }}
                      style={{ marginBottom: 0, fontSize: 13 }}
                      className="hidden md:block"
                    >
                      {display.displayDescription}
                    </Paragraph>
                  ) : null}

                  <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {labels.trigger}: <Tag>{display.displayTrigger}</Tag>
                      {display.displaySchedule ? ` ${display.displaySchedule}` : null}
                    </Text>
                    <Text style={{ fontSize: 13 }} className="hidden md:block">
                      <Text strong>{labels.condition}: </Text>
                      {display.displayCondition}
                    </Text>
                    <Text style={{ fontSize: 13 }} className="hidden md:block">
                      <Text strong>{labels.outcome}: </Text>
                      {display.displayOutcome}
                    </Text>
                  </Space>

                  <Space size={8} wrap>
                    <Link
                      to={`/run-center?scenarioId=${encodeURIComponent(scenario.id)}`}
                      aria-label={labels.runCenterAria(display.displayName)}
                    >
                      {labels.runCenterLink}
                    </Link>
                    {canManage ? (
                      scenario.presetKey === GRAPH_PRESET_KEY ? (
                        <Button
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          aria-label={
                            scenario.isProjected
                              ? labels.createGraph
                              : labels.editAria(display.displayName)
                          }
                          onClick={() =>
                            setGraphEditor({
                              open: true,
                              scenarioId: scenario.isProjected ? null : scenario.id,
                            })
                          }
                          style={{ padding: 0 }}
                        >
                          {scenario.isProjected ? labels.create : labels.edit}
                        </Button>
                      ) : scenario.presetKey === MY_DAY_PRESET_KEY ||
                        scenario.presetKey === CONVERSION_DIGEST_PRESET_KEY ? (
                        // Scheduled presets have no visual editor: the edit flow
                        // only handles step-based configs, which the server
                        // rejects for scheduled scenarios.
                        <Tooltip title={labels.scheduledEditUnavailable}>
                          <span>
                            <Button
                              type="link"
                              size="small"
                              icon={<EditOutlined />}
                              aria-label={labels.editAria(display.displayName)}
                              disabled
                              style={{ padding: 0 }}
                            >
                              {labels.edit}
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          aria-label={labels.editAria(display.displayName)}
                          onClick={() => setSelectedId(scenario.id)}
                          style={{ padding: 0 }}
                        >
                          {labels.edit}
                        </Button>
                      )
                    ) : null}
                  </Space>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>
      <ScenarioEditorDrawer
        scenarioId={selectedId}
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
      />
      <GraphScenarioDrawer
        scenarioId={graphEditor.scenarioId}
        open={graphEditor.open}
        onClose={() => setGraphEditor({ open: false, scenarioId: null })}
      />
    </main>
  );
}
